/**
 * Admin -- Notifications/Rules (Wave 1b W1b-23)
 *
 * Wired to /api/v1/admin/notifications/rules. Mock data has been removed;
 * first paint pulls from the backend (7 default rules are seeded for the
 * F61 admin org by the b43 migration so the page is never empty in dev).
 *
 * Test-fire posts a synthetic event delivery row to notification_deliveries
 * (visible in the Delivery Log tab). Real dispatcher worker is W1b-25
 * (next round).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, Activity, Send, Loader2, Power,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/lib/format';
import { toast } from 'sonner';
import { SectionCard } from '@/components/admin';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';
import { apiClient } from '@/lib/api-client';

// ─── Types (mirror the backend response) ──────────────────────────────────

type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface NotificationRule {
  id: string;
  name: string;
  description: string | null;
  eventPattern: string;
  severityFilter: string[];
  channelIds: string[];
  rateLimitPerHour: number | null;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface NotificationChannel {
  id: string;
  name: string;
  channelType: string;
  isEnabled: boolean;
  itarApproved: boolean;
}

const ALL_SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

const SEVERITY_BADGE: Record<Severity, string> = {
  critical: 'bg-red-500/10 text-red-700 border-red-500/40 dark:text-red-300',
  high: 'bg-orange-500/10 text-orange-700 border-orange-500/40 dark:text-orange-300',
  medium: 'bg-amber-500/10 text-amber-700 border-amber-500/40 dark:text-amber-300',
  low: 'bg-slate-500/10 text-slate-700 border-slate-500/40 dark:text-slate-300',
  info: 'bg-blue-500/10 text-blue-700 border-blue-500/40 dark:text-blue-300',
};

export const Route = createFileRoute('/admin/notifications/')({
  component: RulesPage,
});

function RulesPage() {
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<NotificationRule | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<NotificationRule | null>(null);
  const [firing, setFiring] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [rulesData, channelsData] = await Promise.all([
        apiClient.get<{ rules: NotificationRule[] }>('/admin/notifications/rules'),
        apiClient.get<{ channels: NotificationChannel[] }>('/admin/notifications/channels'),
      ]);
      setRules(rulesData.rules);
      setChannels(channelsData.channels);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load rules';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function toggleRule(id: string) {
    try {
      await apiClient.post(`/admin/notifications/rules/${id}/toggle`, {});
      toast.success('Rule toggled');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Toggle failed';
      toast.error(msg);
    }
  }

  async function deleteRule(id: string) {
    try {
      await apiClient.delete(`/admin/notifications/rules/${id}`);
      toast.success('Rule deleted');
      setDeleting(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  }

  async function testFire(id: string) {
    try {
      setFiring(id);
      const res = await apiClient.post<{ deliveriesCreated: number }>(
        `/admin/notifications/rules/${id}/test-fire`,
        {}
      );
      toast.success(
        `Test fired: ${res.deliveriesCreated} deliveries written. Open the Delivery Log to see them.`
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Test-fire failed';
      toast.error(msg);
    } finally {
      setFiring(null);
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Notification Rules</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Event-pattern routing with severity filtering and rate limiting.
            New tenants start with no rules; click <strong>Add rule</strong> to
            create one. (A 7-rule starter pack is seeded for the F61 fixture
            tenant only — production tenants are intentionally empty until
            configured.)
          </p>
        </div>
        <Button
          size="sm"
          className="h-8 gap-1.5"
          onClick={() => setCreating(true)}
          title="Create a new notification rule"
        >
          <Plus className="h-3.5 w-3.5" />
          Add rule
        </Button>
      </div>

      <SectionCard
        title="Rules"
        icon={Activity}
        description="Each rule routes one or more event patterns to a set of channels. Events are matched against pattern + severity_filter."
      >
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading rules…
          </div>
        ) : rules.length === 0 ? (
          <div className="text-xs text-muted-foreground p-4 italic">
            No rules yet. Click “Add rule” to create one.
          </div>
        ) : (
          <div className="border rounded-md divide-y">
            <div className="grid grid-cols-12 px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/40">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Event pattern</div>
              <div className="col-span-2">Severities</div>
              <div className="col-span-1 text-right">Rate /h</div>
              <div className="col-span-1 text-center">Enabled</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {rules.map((r) => (
              <RuleRow
                key={r.id}
                rule={r}
                channelCount={r.channelIds.length}
                onToggle={() => toggleRule(r.id)}
                onEdit={() => setEditing(r)}
                onDelete={() => setDeleting(r)}
                onTestFire={() => testFire(r.id)}
                firing={firing === r.id}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {creating && (
        <RuleEditorSheet
          mode="create"
          channels={channels}
          open
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}
      {editing && (
        <RuleEditorSheet
          mode="edit"
          rule={editing}
          channels={channels}
          open
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
      {deleting && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setDeleting(null)}
          title="Delete notification rule"
          description={`Permanently delete "${deleting.name}". Existing delivery history is preserved.`}
          consequences={[
            `Rule "${deleting.name}" will be permanently deleted`,
            'Delivery history rows will retain the orphan rule_id (FK SET NULL)',
            'Action is logged to the audit chain',
          ]}
          confirmPhrase={deleting.name}
          confirmLabel="Delete rule"
          onConfirm={() => deleteRule(deleting.id)}
        />
      )}
    </div>
  );
}

function RuleRow({
  rule, channelCount, onToggle, onEdit, onDelete, onTestFire, firing,
}: {
  rule: NotificationRule;
  channelCount: number;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onTestFire: () => void;
  firing: boolean;
}) {
  return (
    <div className="grid grid-cols-12 px-3 py-2.5 items-center text-sm">
      <div className="col-span-3">
        <div className="font-medium truncate" title={rule.name}>{rule.name}</div>
        {rule.description && (
          <div className="text-xs text-muted-foreground truncate" title={rule.description}>
            {rule.description}
          </div>
        )}
      </div>
      <div className="col-span-3">
        <code className="text-xs px-1.5 py-0.5 rounded bg-muted font-mono">
          {rule.eventPattern}
        </code>
        <div className="text-[10px] text-muted-foreground mt-0.5">
          {channelCount} channel{channelCount === 1 ? '' : 's'} · updated {relativeTime(rule.updatedAt)}
        </div>
      </div>
      <div className="col-span-2 flex flex-wrap gap-1">
        {(rule.severityFilter.length > 0 ? rule.severityFilter : ['(all)']).map((s) => (
          <Badge
            key={s}
            variant="outline"
            className={cn(
              'text-[10px] py-0 h-4 px-1.5',
              SEVERITY_BADGE[s as Severity]
            )}
          >
            {s}
          </Badge>
        ))}
      </div>
      <div className="col-span-1 text-right text-xs">
        {rule.rateLimitPerHour ?? <span className="text-muted-foreground">∞</span>}
      </div>
      <div className="col-span-1 flex justify-center">
        <Switch
          checked={rule.isEnabled}
          onCheckedChange={onToggle}
          aria-label="Enable rule"
        />
      </div>
      <div className="col-span-2 flex justify-end gap-1">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          title="Test-fire this rule (writes a synthetic delivery)"
          onClick={onTestFire}
          disabled={!rule.isEnabled || firing}
        >
          {firing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          title="Edit rule"
          onClick={onEdit}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          title="Delete rule"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function RuleEditorSheet({
  mode, rule, channels, open, onClose, onSaved,
}: {
  mode: 'create' | 'edit';
  rule?: NotificationRule;
  channels: NotificationChannel[];
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(rule?.name ?? '');
  const [description, setDescription] = useState(rule?.description ?? '');
  const [eventPattern, setEventPattern] = useState(rule?.eventPattern ?? 'security.*');
  const [severityFilter, setSeverityFilter] = useState<Severity[]>(
    (rule?.severityFilter as Severity[]) ?? ['critical', 'high']
  );
  const [channelIds, setChannelIds] = useState<string[]>(rule?.channelIds ?? []);
  const [rateLimitPerHour, setRateLimitPerHour] = useState<string>(
    rule?.rateLimitPerHour != null ? String(rule.rateLimitPerHour) : ''
  );
  const [isEnabled, setIsEnabled] = useState(rule?.isEnabled ?? true);
  const [saving, setSaving] = useState(false);

  function toggleSeverity(s: Severity) {
    setSeverityFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  }

  function toggleChannel(id: string) {
    setChannelIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  async function save() {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!eventPattern.trim()) {
      toast.error('Event pattern is required');
      return;
    }
    const rate = rateLimitPerHour.trim() === '' ? null : Number(rateLimitPerHour);
    if (rate !== null && (!Number.isFinite(rate) || rate < 1)) {
      toast.error('Rate limit must be a positive integer or empty');
      return;
    }

    const body = {
      name: name.trim(),
      description: description.trim() || undefined,
      eventPattern: eventPattern.trim(),
      severityFilter,
      channelIds,
      rateLimitPerHour: rate,
      isEnabled,
    };

    try {
      setSaving(true);
      if (mode === 'create') {
        await apiClient.post('/admin/notifications/rules', body);
        toast.success('Rule created');
      } else if (rule) {
        await apiClient.put(`/admin/notifications/rules/${rule.id}`, body);
        toast.success('Rule updated');
      }
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[540px] sm:max-w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'Add notification rule' : 'Edit notification rule'}</SheetTitle>
          <SheetDescription>
            Match events by glob pattern + severity, dispatch to one or more channels.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-5">
          <div className="space-y-1.5">
            <Label htmlFor="rule-name">Name</Label>
            <Input
              id="rule-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Security critical events"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rule-desc">Description</Label>
            <Textarea
              id="rule-desc"
              rows={2}
              value={description ?? ''}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Why this rule exists / what it covers"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rule-pattern">Event pattern</Label>
            <Input
              id="rule-pattern"
              value={eventPattern}
              onChange={(e) => setEventPattern(e.target.value)}
              placeholder="security.* or audit.compliance.*"
              className="font-mono"
            />
            <p className="text-[11px] text-muted-foreground">
              Glob compiled by the dispatcher worker. Examples:
              <code className="px-1">security.*</code>,
              <code className="px-1">audit.compliance.itar_*</code>,
              <code className="px-1">service.health.degraded</code>.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Severity filter</Label>
            <div className="flex flex-wrap gap-1.5">
              {ALL_SEVERITIES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSeverity(s)}
                  className={cn(
                    'text-xs px-2 py-1 rounded-md border transition',
                    severityFilter.includes(s)
                      ? cn('font-medium', SEVERITY_BADGE[s])
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  )}
                  title={`Toggle ${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Empty selection = match all severities.
            </p>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Target channels ({channelIds.length} selected)</Label>
            {channels.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No channels yet. Add one on the Channels tab first.
              </p>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto border rounded-md p-2">
                {channels.map((ch) => (
                  <label
                    key={ch.id}
                    className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-muted/60 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={channelIds.includes(ch.id)}
                      onChange={() => toggleChannel(ch.id)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="font-medium">{ch.name}</span>
                    <Badge variant="outline" className="text-[10px] py-0 h-4">{ch.channelType}</Badge>
                    {ch.itarApproved && (
                      <Badge variant="outline" className="text-[10px] py-0 h-4 border-emerald-500/40 text-emerald-700">
                        ITAR
                      </Badge>
                    )}
                    {!ch.isEnabled && (
                      <span className="text-[10px] text-muted-foreground">(disabled)</span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="rule-rate">Rate limit / hour</Label>
              <Input
                id="rule-rate"
                type="number"
                min={1}
                value={rateLimitPerHour}
                onChange={(e) => setRateLimitPerHour(e.target.value)}
                placeholder="(unlimited)"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">
                <Power className="h-3.5 w-3.5" />
                Enabled
              </Label>
              <Switch
                checked={isEnabled}
                onCheckedChange={setIsEnabled}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              {mode === 'create' ? 'Create rule' : 'Save changes'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
